import { useEffect, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Clock, Lock } from 'lucide-react';
import { RecipeImage } from './RecipeImage';
import { RecipeSkeleton, TopBar } from './ui';
import { getRecipe } from '../domain/recipes';
import { config } from '../lib/config';
import { accountService } from '../lib/account';
import { useStore } from '../state/store';

/**
 * Recipes open once the person has a free account: they've already seen what they can make, so
 * this is the moment the account is worth it. Where accounts aren't available (no backend, or the
 * gate is switched off with VITE_RECIPE_GATE=off) the recipe simply opens.
 */
export function RecipeGate({ children }: { children: ReactNode }) {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const account = useStore((s) => s.account);
  const checked = useStore((s) => s.accountChecked);
  const prompt = useStore((s) => s.accountPrompt);
  const requireAccount = useStore((s) => s.requireAccount);
  const gated = config.recipeGate && accountService() !== null && !account;

  // Ask straight away; a signed-in account (or a new one) re-renders into the recipe.
  useEffect(() => {
    if (gated && checked && !prompt) requireAccount('open this recipe', () => {});
  }, [gated, checked]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!gated) return <>{children}</>;
  if (!checked) return <RecipeSkeleton />;

  const recipe = getRecipe(id);
  return (
    <main className="min-h-[100dvh]">
      <div className="max-w-[640px] mx-auto w-full">
        <div className="screen pb-0"><TopBar back={() => navigate(-1)} /></div>
        {recipe && <RecipeImage recipeId={recipe.id} ratio="4/3" rounded="rounded-none sm:rounded-[24px]" />}
        <div className="screen pt-5">
          {recipe && (
            <>
              <h1 className="text-title-1 mb-1">{recipe.title}</h1>
              <p className="inline-flex items-center gap-1 text-subhead text-fg-muted mb-5"><Clock size={15} aria-hidden /> {recipe.minutes} min · {recipe.stepCount} steps</p>
            </>
          )}
          <p className="flex items-start gap-2 text-body mb-5"><Lock size={18} className="text-primary mt-[3px] shrink-0" aria-hidden />Create a free account to open the full recipe. Your picks and kitchen come with you.</p>
          <button className="btn btn-primary w-full" onClick={() => requireAccount('open this recipe', () => {})}>Create a free account or sign in</button>
        </div>
      </div>
    </main>
  );
}
